/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * ⌘K / Ctrl+K command palette. Two sources, both already permission-filtered:
 * pages from the same sidebar list the user sees, and records from the same
 * GET /search the classic navbar search uses (including its travel filtering
 * and result routing, via utils/globalSearch). Recent records are kept in
 * this browser only.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import InputBase from "@mui/material/InputBase";
import Typography from "@mui/material/Typography";
import { ArrowUpRight, Clock, Search } from "lucide-react";
import { useAuth } from "auth/Auth";
import { useTravelUi } from "hooks/useTravelUi";
import { filterSearchResults, getResultText, getTypeLabel, searchResultPath } from "utils/globalSearch";
import { v2 as t } from "styles/tokens/v2";
import { NavIcon } from "./navIcons";

const RECENT_KEY = "pearl.v2.recentRecords";
const MAX_RECENT = 6;
const MAX_RECORDS = 8;
const MAX_PAGES = 6;

function readRecent() {
  try {
    const list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function pushRecent(entry) {
  try {
    const next = [entry, ...readRecent().filter((r) => r.path !== entry.path)].slice(0, MAX_RECENT);
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Storage blocked: recents are a convenience only.
  }
}

export default function CommandPalette({ open, onClose, pages }) {
  const navigate = useNavigate();
  const { axiosInstance } = useAuth();
  const { showTravel } = useTravelUi();
  const [query, setQuery] = useState("");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const listRef = useRef(null);
  const trimmed = query.trim();

  useEffect(() => {
    if (!open) {
      setQuery("");
      setRecords([]);
      setHighlight(0);
    }
  }, [open]);

  useEffect(() => {
    if (trimmed.length < 2 || !axiosInstance) {
      setRecords([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await axiosInstance.get(`/search?query=${encodeURIComponent(trimmed)}`);
        if (!cancelled) setRecords(filterSearchResults(data, showTravel, { keepLegacyCommerce: true }).filter((r) => searchResultPath(r)));
      } catch {
        // /search answers 404 when nothing matches.
        if (!cancelled) setRecords([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [trimmed, axiosInstance, showTravel]);

  const matchingPages = useMemo(() => {
    if (!trimmed) return [];
    const q = trimmed.toLowerCase();
    return pages.filter((p) => p.route && (p.name || "").toLowerCase().includes(q)).slice(0, MAX_PAGES);
  }, [pages, trimmed]);

  const entries = useMemo(() => {
    if (!trimmed) {
      return readRecent().map((r) => ({ group: "Recent records", ...r, recent: true }));
    }
    const pageEntries = matchingPages.map((p) => ({
      group: "Pages",
      id: `page-${p.key}`,
      label: p.name,
      sub: p.route,
      path: p.route,
      icon: p.icon,
    }));
    const recordEntries = records.slice(0, MAX_RECORDS).map((r) => ({
      group: "Records",
      id: `${r.type}-${r.id}`,
      label: getResultText(r) || `${getTypeLabel(r.type)} #${r.id}`,
      sub: getTypeLabel(r.type),
      path: searchResultPath(r),
      record: true,
    }));
    const all = [...pageEntries, ...recordEntries];
    if (trimmed.length >= 2) {
      all.push({
        group: "Search",
        id: "view-all",
        label: `See all results for “${trimmed}”`,
        path: `/search?query=${encodeURIComponent(trimmed)}`,
      });
    }
    return all;
  }, [trimmed, matchingPages, records]);

  useEffect(() => setHighlight(0), [entries.length]);

  const choose = useCallback(
    (entry) => {
      if (!entry?.path) return;
      if (entry.record) pushRecent({ id: entry.id, label: entry.label, sub: entry.sub, path: entry.path });
      onClose();
      navigate(entry.path);
    },
    [navigate, onClose]
  );

  const onKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlight((h) => Math.min(h + 1, entries.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      choose(entries[highlight]);
    }
  };

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${highlight}"]`);
    el?.scrollIntoView?.({ block: "nearest" });
  }, [highlight]);

  let lastGroup = null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      aria-label="Search and jump to"
      PaperProps={{ sx: { width: 640, maxWidth: "calc(100% - 24px)", position: "absolute", top: 88, m: 0, borderRadius: "12px" } }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2, height: 52, borderBottom: `1px solid ${t.border}` }}>
        <Search size={18} color={t.text3} aria-hidden="true" />
        <InputBase
          autoFocus
          fullWidth
          placeholder="Search records or jump to a page"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          inputProps={{ "aria-label": "Search", role: "combobox", "aria-expanded": true, "aria-controls": "v2-palette-list" }}
          sx={{ fontSize: 16, fontFamily: "inherit" }}
        />
        {loading && <CircularProgress size={16} />}
        <Box component="kbd" sx={{ fontSize: 11, color: t.text3, border: `1px solid ${t.border}`, borderRadius: "4px", px: 0.75, fontFamily: "inherit" }}>
          esc
        </Box>
      </Box>

      <Box id="v2-palette-list" role="listbox" ref={listRef} sx={{ maxHeight: 460, overflowY: "auto", py: 1 }}>
        {entries.length === 0 && (
          <Typography sx={{ px: 2.5, py: 3, fontSize: 14, color: t.text2 }}>
            {trimmed.length < 2 ? "Type at least two characters to search records." : loading ? "Searching…" : "No matches."}
          </Typography>
        )}
        {entries.map((entry, index) => {
          const header = entry.group !== lastGroup ? entry.group : null;
          lastGroup = entry.group;
          const active = index === highlight;
          return (
            <Box key={entry.id || entry.path}>
              {header && (
                <Typography sx={{ px: 2.5, pt: 1.25, pb: 0.5, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: t.text3 }}>
                  {header}
                </Typography>
              )}
              <Box
                role="option"
                aria-selected={active}
                data-index={index}
                onMouseEnter={() => setHighlight(index)}
                onClick={() => choose(entry)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mx: 1,
                  px: 1.5,
                  py: 1,
                  borderRadius: "8px",
                  cursor: "pointer",
                  bgcolor: active ? t.hover : "transparent",
                }}
              >
                <Box sx={{ width: 30, height: 30, borderRadius: "8px", bgcolor: t.surface2, border: `1px solid ${t.border}`, display: "grid", placeItems: "center", color: t.text2, flexShrink: 0 }}>
                  {entry.recent ? <Clock size={15} /> : entry.icon ? <NavIcon icon={entry.icon} size={15} /> : <ArrowUpRight size={15} />}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: t.text }} noWrap>
                    {entry.label}
                  </Typography>
                  {entry.sub && (
                    <Typography sx={{ fontSize: 12, color: t.text2 }} noWrap>
                      {entry.sub}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ px: 2.5, py: 1, borderTop: `1px solid ${t.border}`, fontSize: 12, color: t.text3 }}>
        ↑ ↓ to move · ↵ to open · esc to close
      </Box>
    </Dialog>
  );
}

CommandPalette.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  pages: PropTypes.arrayOf(PropTypes.object).isRequired,
};
